import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import disponibilidadeService from "../../../../../service/disponibilidade";
import designacaoService from "../../../../../service/designacao";
import eventosService from "../../../../../service/eventos/EventosService";
import ausenciaService from "../../../../../service/ausencia";
import {
  ITEMS_PER_PAGE,
  STATUS_IGNORADOS_FREQUENCIA,
  getWeekOfMonth,
  getOccurrencesInMonth,
  getAllowedWeeksForFrequency,
  normalize,
  isTorreDeTVRegion,
  pontoMatchesSelection,
  getPeriodoFromHorarios,
  agruparDisponibilidadesPorUsuario,
  filtrarDisponibilidades,
  filtrarPorBusca,
  agruparEOrdenarPublicadores,
  getPublicadoresElegiveis,
  removerDisponibilidadesDuplicadas,
} from '../utils/ModalDesignacao.utils';

// ============================================================================
// CONTEXT
// ============================================================================

const ModalDesignacaoContext = createContext(null);

// ============================================================================
// FUNÇÕES AUXILIARES
// ============================================================================

/**
 * Converte uma data ISO (UTC) para Date local, evitando erro de fuso horário.
 * Ex.: "2026-02-04T00:00:00.000Z" em UTC-3 seria terça-feira sem essa correção.
 */
function parseLocalDate(dateInput) {
  if (!dateInput) return null;
  const str = dateInput instanceof Date ? dateInput.toISOString() : String(dateInput);
  const datePart = str.substring(0, 10); // "YYYY-MM-DD"
  const [year, month, day] = datePart.split('-').map(Number);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day); // meia-noite local
}

function timeStringToMinutes(timeValue) {
  if (!timeValue || typeof timeValue !== 'string') return null;
  const match = timeValue.match(/^(\d{1,2}):(\d{2})$/);
  if (!match) return null;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) return null;
  return (hours * 60) + minutes;
}

function getIntervaloHorarioDesignacao(horario) {
  const intervalos = {
    '6h - 8h': ['06:00', '08:00'],
    '8h - 10h': ['08:00', '10:00'],
    '10h - 12h': ['10:00', '12:00'],
    '12h - 14h': ['12:00', '14:00'],
    '14h - 16h': ['14:00', '16:00'],
    '16h - 18h': ['16:00', '18:00'],
    '18h - 20h': ['18:00', '20:00'],
  };

  const intervalo = intervalos[horario];
  if (!intervalo) return null;

  return {
    inicio: timeStringToMinutes(intervalo[0]),
    fim: timeStringToMinutes(intervalo[1]),
  };
}

function intervalsOverlap(startA, endA, startB, endB) {
  if ([startA, endA, startB, endB].some((value) => value === null)) return false;
  return startA < endB && startB < endA;
}

function processarEventosInscritos(todosEventos) {
  const eventosPorPublicador = {};

  if (!Array.isArray(todosEventos)) {
    return eventosPorPublicador;
  }

  todosEventos.forEach((evento) => {
    const turnosEvento = Array.isArray(evento?.turnos) ? evento.turnos : [];
    const horariosPermitidos = Array.isArray(evento?.horariosPermitidos) ? evento.horariosPermitidos : [];
    const inscritos = Array.isArray(evento?.inscritos) ? evento.inscritos : [];

    inscritos.forEach((inscrito) => {
      if (!inscrito?._id || inscrito?.status !== 'inscrito') return;

      const turnoIdsInscricao = Array.isArray(inscrito.turnos)
        ? inscrito.turnos.map((turno) => String(turno?._id || turno))
        : [];

      let turnosRelacionados = turnosEvento.filter((turno) => turnoIdsInscricao.includes(String(turno?._id)));

      if (turnosRelacionados.length === 0 && turnosEvento.length > 0) {
        turnosRelacionados = turnosEvento;
      }

      if (turnosRelacionados.length === 0 && horariosPermitidos.length > 0) {
        turnosRelacionados = horariosPermitidos.map((faixa) => {
          const [horarioInicial, horarioFinal] = String(faixa).split('-');
          return {
            horarioInicial: horarioInicial?.trim(),
            horarioFinal: horarioFinal?.trim(),
          };
        });
      }

      if (!eventosPorPublicador[inscrito._id]) {
        eventosPorPublicador[inscrito._id] = [];
      }

      eventosPorPublicador[inscrito._id].push({
        nome: evento?.nome || evento?.titulo || 'Evento',
        data: evento?.data,
        turnos: turnosRelacionados,
      });
    });
  });

  return eventosPorPublicador;
}

function verificarConflitoEvento({
  eventosPublicador,
  dataSelecionada,
  horariosSelecionados,
}) {
  if (!Array.isArray(eventosPublicador) || eventosPublicador.length === 0) return null;
  if (!(dataSelecionada instanceof Date) || Number.isNaN(dataSelecionada.getTime())) return null;
  if (!Array.isArray(horariosSelecionados) || horariosSelecionados.length === 0) return null;

  return eventosPublicador.find((evento) => {
    const dataEvento = parseLocalDate(evento?.data);
    if (!dataEvento || Number.isNaN(dataEvento.getTime())) return false;

    const mesmaData =
      dataEvento.getFullYear() === dataSelecionada.getFullYear() &&
      dataEvento.getMonth() === dataSelecionada.getMonth() &&
      dataEvento.getDate() === dataSelecionada.getDate();

    if (!mesmaData) return false;

    return horariosSelecionados.some((horarioDesignacao) => {
      const intervaloDesignacao = getIntervaloHorarioDesignacao(horarioDesignacao);
      if (!intervaloDesignacao) return false;

      return (evento?.turnos || []).some((turno) => {
        const inicioEvento = timeStringToMinutes(turno?.horarioInicial);
        const fimEvento = timeStringToMinutes(turno?.horarioFinal);
        return intervalsOverlap(
          intervaloDesignacao.inicio,
          intervaloDesignacao.fim,
          inicioEvento,
          fimEvento
        );
      });
    });
  }) || null;
}

function processarDesignacoes(disponibilidades, todasDesignacoes) {
  const mapUltimas = {};
  const mapDesignacoes = {};

  disponibilidades.forEach((disp) => {
    if (disp.usuario && disp.usuario._id) {
      mapDesignacoes[disp.usuario._id] = [];
      mapUltimas[disp.usuario._id] = null;
    }
  });

  if (Array.isArray(todasDesignacoes)) {
    todasDesignacoes.forEach((des) => {
      if (des.publicadores && Array.isArray(des.publicadores)) {
        des.publicadores.forEach((pub) => {
          const uid = pub && pub._id ? pub._id : pub;
          if (uid && mapDesignacoes[uid]) {
            mapDesignacoes[uid].push(des);
          }
        });
      }
    });
  }

  Object.keys(mapDesignacoes).forEach((userId) => {
    const listaDesignacoes = mapDesignacoes[userId];
    if (listaDesignacoes.length > 0) {
      const maisRecente = listaDesignacoes.reduce((max, atual) => {
        if (!atual || !atual.data) return max;
        if (!max || !max.data) return atual;
        return new Date(atual.data).getTime() > new Date(max.data).getTime() ? atual : max;
      }, listaDesignacoes[0]);
      mapUltimas[userId] = maisRecente?.data || null;
    }
  });

  return { mapUltimas, mapDesignacoes };
}

function verificarDesignacaoNaSemana({
  designacoesPublicador,
  pontoSelecionado,
  ano,
  mes,
  weekday,
  semanaAtual,
  horarioSelecionado,
}) {
  return designacoesPublicador.some((designacao) => {
    if (!designacao) return false;
    if (STATUS_IGNORADOS_FREQUENCIA.includes(designacao.status)) return false;
    if (!pontoMatchesSelection(designacao.ponto, pontoSelecionado)) return false;

    const dataDesignacao = designacao.data ? parseLocalDate(designacao.data) : null;
    if (!dataDesignacao || Number.isNaN(dataDesignacao.getTime())) return false;
    if (dataDesignacao.getFullYear() !== ano || dataDesignacao.getMonth() !== mes) return false;
    if (dataDesignacao.getDay() !== weekday) return false;

    const semanaDesignacao = getWeekOfMonth(dataDesignacao);
    if (semanaDesignacao !== semanaAtual) return false;

    const horariosDesignacao = Array.isArray(designacao.horario) ? designacao.horario : [];
    return horariosDesignacao.includes(horarioSelecionado);
  });
}

function verificarCotaFrequencia({
  designacoesPublicador,
  pontoSelecionado,
  ano,
  mes,
  weekday,
  horarioSelecionado,
  semanasPermitidas,
  semanaAtual,
}) {
  const relevantes = designacoesPublicador.filter((designacao) => {
    if (!designacao) return false;
    if (STATUS_IGNORADOS_FREQUENCIA.includes(designacao.status)) return false;
    if (!pontoMatchesSelection(designacao.ponto, pontoSelecionado)) return false;

    const dataDesignacao = designacao.data ? parseLocalDate(designacao.data) : null;
    if (!dataDesignacao || Number.isNaN(dataDesignacao.getTime())) return false;
    if (dataDesignacao.getFullYear() !== ano || dataDesignacao.getMonth() !== mes) return false;
    if (dataDesignacao.getDay() !== weekday) return false;

    const horariosDesignacao = Array.isArray(designacao.horario) ? designacao.horario : [];
    return horariosDesignacao.includes(horarioSelecionado);
  });

  const contagemPorSemana = relevantes.reduce((acc, designacao) => {
    const dataDesignacao = new Date(designacao.data);
    const semana = getWeekOfMonth(dataDesignacao);
    if (!semanasPermitidas.has(semana)) return acc;
    acc.set(semana, (acc.get(semana) || 0) + 1);
    return acc;
  }, new Map());

  return (contagemPorSemana.get(semanaAtual) || 0) >= 1;
}

function verificarHorarioDisponivel({
  horarioSelecionado,
  disponibilidadesUsuario,
  pontoSelecionado,
  diaSelecionado,
  ocorrencias,
  semanaAtual,
  designacoesPorPublicador,
  usuarioId,
  ano,
  mes,
  weekday,
}) {
  for (const disp of disponibilidadesUsuario) {
    if (!pontoMatchesSelection(disp.ponto, pontoSelecionado)) continue;
    if (diaSelecionado && normalize(disp.dia) !== normalize(diaSelecionado)) continue;

    const horariosDisp = Array.isArray(disp.horario) ? disp.horario : [];
    if (!horariosDisp.includes(horarioSelecionado)) continue;

    const frequencia = disp.frequencia || 'Todas as semanas do mês';
    const semanasPermitidas = getAllowedWeeksForFrequency(frequencia, ocorrencias);
    
    // Se a semana atual não está nas semanas permitidas pela frequência, pula
    if (semanasPermitidas.size === 0 || !semanasPermitidas.has(semanaAtual)) continue;

    const designacoesPublicador = designacoesPorPublicador[usuarioId] || [];

    // Verifica se já usou a cota para esta semana
    const jaUsouCota = verificarCotaFrequencia({
      designacoesPublicador,
      pontoSelecionado,
      ano,
      mes,
      weekday,
      horarioSelecionado,
      semanasPermitidas,
      semanaAtual,
    });

    if (!jaUsouCota) return true;
  }

  return false;
}

function calcularDisponibilidadeSemana({
  dataSelecionada,
  pontoSelecionado,
  periodoSelecionado,
  horariosSelecionados,
  diaSelecionado,
  disponibilidades,
  designacoesPorPublicador,
  eventosPorPublicador,
}) {
  const novaDispSemana = {};

  const data = dataSelecionada instanceof Date
    ? dataSelecionada
    : dataSelecionada ? new Date(dataSelecionada) : null;

  if (!data || Number.isNaN(data.getTime())) return novaDispSemana;
  if (!pontoSelecionado || !periodoSelecionado) return novaDispSemana;
  if (!Array.isArray(horariosSelecionados) || horariosSelecionados.length === 0) return novaDispSemana;

  const semanaAtual = getWeekOfMonth(data);
  if (semanaAtual === null) return novaDispSemana;

  const ocorrencias = getOccurrencesInMonth(data);
  const weekday = data.getDay();
  const ano = data.getFullYear();
  const mes = data.getMonth();

  const isTorreDeTVSelection = pontoSelecionado && isTorreDeTVRegion(pontoSelecionado);

  // Importante: filtrar ANTES de agrupar/deduplicar.
  // Caso contrário, a deduplicação (que ignora ponto) pode eliminar uma disponibilidade válida
  // e manter outra de uma região diferente, causando falso "Não disponível nesta semana do mês".
  let disponibilidadesRelevantes = filtrarDisponibilidades(disponibilidades, {
    diaSelecionado,
    pontoSelecionado: isTorreDeTVSelection ? null : pontoSelecionado,
  });

  if (isTorreDeTVSelection) {
    disponibilidadesRelevantes = disponibilidadesRelevantes.filter((disp) => isTorreDeTVRegion(disp.ponto));
  }

  const dispPorUsuario = agruparDisponibilidadesPorUsuario(disponibilidadesRelevantes);

  Object.keys(dispPorUsuario).forEach((usuarioId) => {
    const disponibilidadesUsuario = dispPorUsuario[usuarioId];
    const horariosNaoDisponiveis = [];

    const conflitoEvento = verificarConflitoEvento({
      eventosPublicador: eventosPorPublicador[usuarioId] || [],
      dataSelecionada: data,
      horariosSelecionados,
    });

    if (conflitoEvento) {
      novaDispSemana[usuarioId] = {
        disponivel: false,
        mensagem: `Inscrito no evento ${conflitoEvento.nome}`,
      };
      return;
    }

    for (const horarioSelecionado of horariosSelecionados) {
      const horarioDisponivelNaSemana = verificarHorarioDisponivel({
        horarioSelecionado,
        disponibilidadesUsuario,
        pontoSelecionado,
        diaSelecionado,
        ocorrencias,
        semanaAtual,
        designacoesPorPublicador,
        usuarioId,
        ano,
        mes,
        weekday,
      });

      if (!horarioDisponivelNaSemana) {
        horariosNaoDisponiveis.push(horarioSelecionado);
      }
    }

    if (horariosNaoDisponiveis.length > 0) {
      novaDispSemana[usuarioId] = {
        disponivel: false,
        mensagem: "Não disponível nesta semana do mês",
      };
    }
  });

  return novaDispSemana;
}

/**
 * Agrupa as ausências pelo ID do publicador.
 */
function agruparAusenciasPorPublicador(ausencias) {
  const porPublicador = {};

  if (!Array.isArray(ausencias)) return porPublicador;

  ausencias.forEach((ausencia) => {
    const usuarioId = ausencia?.publicador?._id || ausencia?.publicador;
    if (!usuarioId) return;

    if (!porPublicador[usuarioId]) porPublicador[usuarioId] = [];
    porPublicador[usuarioId].push(ausencia);
  });

  return porPublicador;
}

/**
 * Verifica se alguma ausência do publicador cobre a data/horário selecionados.
 * Mesma lógica de sobreposição usada no backend (ausencia.service.js#verificarAusencia).
 */
function encontrarAusenciaConflitante({
  ausenciasPublicador,
  dataSelecionada,
  horariosSelecionados,
}) {
  if (!Array.isArray(ausenciasPublicador) || ausenciasPublicador.length === 0) return null;
  if (!(dataSelecionada instanceof Date) || Number.isNaN(dataSelecionada.getTime())) return null;

  return ausenciasPublicador.find((ausencia) => {
    const inicio = parseLocalDate(ausencia?.dataInicio);
    const fim = parseLocalDate(ausencia?.dataTermino);
    if (!inicio || !fim) return false;
    if (dataSelecionada < inicio || dataSelecionada > fim) return false;

    if (ausencia.todoODia) return true;

    if (!Array.isArray(horariosSelecionados) || horariosSelecionados.length === 0) return true;

    const inicioAusencia = timeStringToMinutes(ausencia.horarioInicio);
    const fimAusencia = timeStringToMinutes(ausencia.horarioTermino);

    return horariosSelecionados.some((horarioDesignacao) => {
      const intervaloDesignacao = getIntervaloHorarioDesignacao(horarioDesignacao);
      if (!intervaloDesignacao) return false;
      return intervalsOverlap(
        intervaloDesignacao.inicio,
        intervaloDesignacao.fim,
        inicioAusencia,
        fimAusencia
      );
    });
  }) || null;
}

/**
 * Calcula, para a data/horário selecionados, qual ausência (se houver) afeta cada publicador.
 */
function calcularAusenciasSelecionadas({
  dataSelecionada,
  horariosSelecionados,
  ausenciasPorPublicador,
}) {
  const resultado = {};

  // Normaliza para meia-noite local, já que inicio/fim da ausência também são
  // comparados em meia-noite local (parseLocalDate) — evita falso positivo/negativo
  // no último dia do período por causa da hora do dia.
  const data = parseLocalDate(dataSelecionada);

  if (!data || Number.isNaN(data.getTime())) return resultado;

  Object.keys(ausenciasPorPublicador).forEach((usuarioId) => {
    const conflito = encontrarAusenciaConflitante({
      ausenciasPublicador: ausenciasPorPublicador[usuarioId],
      dataSelecionada: data,
      horariosSelecionados,
    });

    if (conflito) {
      resultado[usuarioId] = conflito;
    }
  });

  return resultado;
}

// ============================================================================
// PROVIDER
// ============================================================================

export const ModalDesignacaoProvider = ({
  children,
  open,
  horariosSelecionados = [],
  diaSelecionado = null,
  pontoSelecionado = null,
  dataSelecionada = null,
}) => {
  // Estados
  const [disponibilidades, setDisponibilidades] = useState([]);
  const [page, setPage] = useState(1);
  const [ultimasDesignacoes, setUltimasDesignacoes] = useState({});
  const [search, setSearch] = useState("");
  const [filtroDisponibilidade, setFiltroDisponibilidade] = useState("Todos");
  const [designacoesPorPublicador, setDesignacoesPorPublicador] = useState({});
  const [eventosPorPublicador, setEventosPorPublicador] = useState({});
  const [limitesFrequencia, setLimitesFrequencia] = useState({});
  const [disponibilidadeSemana, setDisponibilidadeSemana] = useState({});
  const [ausenciasPorPublicador, setAusenciasPorPublicador] = useState({});
  const [ausenciasSelecionadas, setAusenciasSelecionadas] = useState({});
  const [carregando, setCarregando] = useState(true);
  
  const [selecionados, setSelecionados] = useState([]);

  // Memoizações
  const periodoSelecionado = useMemo(
    () => getPeriodoFromHorarios(horariosSelecionados),
    [horariosSelecionados]
  );

  // Helpers
  const resetarEstados = useCallback(() => {
    setDisponibilidades([]);
    setUltimasDesignacoes({});
    setDesignacoesPorPublicador({});
    setEventosPorPublicador({});
    setLimitesFrequencia({});
    setAusenciasPorPublicador({});
    setSelecionados([]);
  }, []);

  // Fetch de dados iniciais
  useEffect(() => {
    if (!open) {
      setCarregando(true);
      return;
    }

    const fetchData = async () => {
      setCarregando(true);
      try {
        const [lista, todasDesignacoes, todosEventos, todasAusencias] = await Promise.all([
          disponibilidadeService.getDisponibilidades(),
          designacaoService.getDesignacoes(),
          eventosService.getEventos(),
          ausenciaService.getAusencias(),
        ]);

        setDisponibilidades(lista);
        setPage(1);

        const { mapUltimas, mapDesignacoes } = processarDesignacoes(lista, todasDesignacoes);

        setUltimasDesignacoes(mapUltimas);
        setDesignacoesPorPublicador(mapDesignacoes);
        setEventosPorPublicador(processarEventosInscritos(todosEventos));
        setAusenciasPorPublicador(agruparAusenciasPorPublicador(todasAusencias));
        setLimitesFrequencia({});
      } catch (error) {
        console.error("Erro ao carregar dados no modal:", error);
        resetarEstados();
      } finally {
        setCarregando(false);
      }
    };

    fetchData();
  }, [open, resetarEstados]);

  // Cálculo de disponibilidade por semana
  useEffect(() => {
    if (!open || carregando) {
      setLimitesFrequencia({});
      setDisponibilidadeSemana({});
      return;
    }

    const novaDispSemana = calcularDisponibilidadeSemana({
      dataSelecionada,
      pontoSelecionado,
      periodoSelecionado,
      horariosSelecionados,
      diaSelecionado,
      disponibilidades,
      designacoesPorPublicador,
      eventosPorPublicador,
    });

    setLimitesFrequencia({});
    setDisponibilidadeSemana(novaDispSemana);
  }, [
    open,
    carregando,
    dataSelecionada,
    pontoSelecionado,
    periodoSelecionado,
    horariosSelecionados,
    disponibilidades,
    designacoesPorPublicador,
    eventosPorPublicador,
    diaSelecionado,
  ]);

  // Cálculo de ausências que conflitam com a data/horário selecionados
  useEffect(() => {
    if (!open || carregando) {
      setAusenciasSelecionadas({});
      return;
    }

    setAusenciasSelecionadas(
      calcularAusenciasSelecionadas({
        dataSelecionada,
        horariosSelecionados,
        ausenciasPorPublicador,
      })
    );
  }, [open, carregando, dataSelecionada, horariosSelecionados, ausenciasPorPublicador]);

  // Dados computados
  const publicadoresData = useMemo(() => {
    // Determina se estamos filtrando por Torre de TV
    const isTorreDeTVSelection = pontoSelecionado && isTorreDeTVRegion(pontoSelecionado);

    // Para Torre de TV: não filtrar por ponto específico na primeira etapa
    let dispsFiltradas = filtrarDisponibilidades(disponibilidades, {
      diaSelecionado,
      pontoSelecionado: isTorreDeTVSelection ? null : pontoSelecionado,
    });

    // Se for Torre de TV, precisamos garantir que só consideramos disponibilidades
    // de pontos que SÃO da Torre de TV (qualquer um deles), ignorando pontos de outras regiões
    if (isTorreDeTVSelection) {
      dispsFiltradas = dispsFiltradas.filter(disp => isTorreDeTVRegion(disp.ponto));
    }

    const dispsPorUsuario = agruparDisponibilidadesPorUsuario(dispsFiltradas);
    const publicadoresElegiveis = getPublicadoresElegiveis(dispsPorUsuario, horariosSelecionados);

    // Filtra disponibilidades dos publicadores elegíveis
    let disponibilidadesFiltradas = disponibilidades.filter((disp) => {
      const usuarioId = disp.usuario?._id;
      if (!usuarioId || !publicadoresElegiveis.has(usuarioId)) return false;
      if (diaSelecionado && normalize(disp.dia) !== normalize(diaSelecionado)) return false;
      
      // Aplica filtro de ponto com lógica especial para Torre de TV
      if (pontoSelecionado && !pontoMatchesSelection(disp.ponto, pontoSelecionado)) return false;
      
      return true;
    });

    // Remove duplicatas visuais (mesmo horário em pontos diferentes da mesma região)
    disponibilidadesFiltradas = removerDisponibilidadesDuplicadas(disponibilidadesFiltradas);

    disponibilidadesFiltradas = filtrarPorBusca(disponibilidadesFiltradas, search);
    let result = agruparEOrdenarPublicadores(disponibilidadesFiltradas);
    
    if (filtroDisponibilidade !== "Todos") {
      result = result.filter(pub => {
        const usuarioId = pub.usuario?._id;
        const limite = limitesFrequencia[usuarioId];
        const dispSemana = disponibilidadeSemana[usuarioId];
        const ausente = Boolean(ausenciasSelecionadas[usuarioId]);
        const disabled = Boolean((limite && limite.atingido) || (dispSemana && !dispSemana.disponivel) || ausente);

        if (filtroDisponibilidade === "Disponíveis") {
          return !disabled;
        } else if (filtroDisponibilidade === "Indisponíveis") {
          return disabled;
        }
        return true;
      });
    }

    return result;
  }, [disponibilidades, diaSelecionado, pontoSelecionado, horariosSelecionados, search, filtroDisponibilidade, limitesFrequencia, disponibilidadeSemana, ausenciasSelecionadas]);

  // Paginação
  const paginationData = useMemo(() => {
    const totalItems = publicadoresData.length;
    const totalPages = Math.max(1, Math.ceil(totalItems / ITEMS_PER_PAGE));
    const startIdx = (page - 1) * ITEMS_PER_PAGE;
    const endIdx = startIdx + ITEMS_PER_PAGE;
    const currentPageItems = publicadoresData.slice(startIdx, endIdx);

    return {
      totalPages,
      totalItems,
      currentPageItems,
      displayPage: !pontoSelecionado ? 1 : page,
      displayTotalPages: !pontoSelecionado ? 1 : totalPages,
      hasNextPage: page < totalPages,
      hasPrevPage: page > 1,
    };
  }, [publicadoresData, page, pontoSelecionado]);

  // Handlers
  const handleNextPage = useCallback(() => {
    if (page < paginationData.totalPages) setPage((p) => p + 1);
  }, [page, paginationData.totalPages]);

  const handlePrevPage = useCallback(() => {
    if (page > 1) setPage((p) => p - 1);
  }, [page]);

  const handleSearch = useCallback((value) => {
    setSearch(value);
    setPage(1);
  }, []);

  const handleFilterChange = useCallback((value) => {
    setFiltroDisponibilidade(value);
    setPage(1);
  }, []);

  const togglePublicador = useCallback((publicador) => {
    setSelecionados((prev) => {
      const isSelected = prev.some((p) => p._id === publicador._id);
      if (isSelected) {
        return prev.filter((p) => p._id !== publicador._id);
      } else {
        if (prev.length < 3) {
          return [...prev, publicador];
        }
        return prev;
      }
    });
  }, []);

  // Value do context
  const value = useMemo(() => ({
    carregando,
    search,
    filtroDisponibilidade,
    handleFilterChange,
    ultimasDesignacoes,
    limitesFrequencia,
    disponibilidadeSemana,
    ausenciasSelecionadas,
    publicadoresData,
    paginationData,
    selecionados,
    togglePublicador,
    handleNextPage,
    handlePrevPage,
    handleSearch,
  }), [
    carregando,
    search,
    filtroDisponibilidade,
    ultimasDesignacoes,
    limitesFrequencia,
    disponibilidadeSemana,
    ausenciasSelecionadas,
    publicadoresData,
    paginationData,
    selecionados,
    togglePublicador,
    handleFilterChange,
    handleNextPage,
    handlePrevPage,
    handleSearch,
  ]);

  return (
    <ModalDesignacaoContext.Provider value={value}>
      {children}
    </ModalDesignacaoContext.Provider>
  );
};

// ============================================================================
// HOOK DE ACESSO AO CONTEXT
// ============================================================================

export const useModalDesignacaoContext = () => {
  const context = useContext(ModalDesignacaoContext);

  if (!context) {
    throw new Error(
      'useModalDesignacaoContext deve ser usado dentro de um ModalDesignacaoProvider'
    );
  }

  return context;
};

export default ModalDesignacaoProvider;
